import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Cookie, FileText, Landmark, LockKeyhole, Scale, ShieldCheck, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useSettings } from "@/context/AppSettings";

const UAE_PDPL_URL = "https://u.ae/en/about-the-uae/digital-uae/data/data-protection-laws";
const UAE_KYC_URL = "https://rulebook.centralbank.ae/en/rulebook/guidance-licensed-financial-institutions-customer-due-diligenceknow-your-customer-and";

export const LegalComplianceModal = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("company");
  const { lang } = useLang();
  const settings = useSettings();
  const isRu = lang === "ru";
  const email = settings.email || "info@melnikbrokers.ae";
  const company = settings.legal_name || settings.company_name || "MELNIK COMMERCIAL BROKERS";
  const licence = settings.license_number || "1656588";
  const address = settings.address || "Dubai, United Arab Emirates";

  useEffect(() => {
    if (!open) return undefined;
    const originalOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const content = useMemo(() => {
    const shared = {
      company,
      licence,
      address,
      email,
    };

    if (isRu) {
      return {
        heading: "Юридическая информация и комплаенс",
        eyebrow: "MELNIK Commercial Brokers · Dubai, U.A.E.",
        trigger: "Юридическая информация",
        close: "Закрыть",
        note: "Публичная справочная информация. Для конкретной сделки применяются подписанные сторонами документы и требования применимого права.",
        sections: [
          {
            id: "company",
            icon: Landmark,
            label: "Компания и роль",
            title: "Статус коммерческого брокера",
            paragraphs: [
              `${shared.company} действует как коммерческий брокер в Дубае. Лицензия: ${shared.licence}. Контактный адрес: ${shared.address}.`,
              "Компания организует деловые знакомства и помогает сторонам структурировать коммуникацию и проверку контрагентов. Она не выступает продавцом или покупателем товара, не принимает товар на хранение, не распоряжается средствами сторон и не гарантирует заключение либо исполнение сделки. Если стороны обсуждают SBLC, escrow или коммерческий дисконт к согласованному рыночному ориентиру, такие условия определяют только сами стороны, их банки, уполномоченный escrow-провайдер и профессиональные консультанты.",
              "Информация на сайте носит предварительный и справочный характер и не является публичной офертой, инвестиционной рекомендацией, предложением финансовой услуги или обязательством совершить сделку. Коммерческие, расчётные и поставочные условия определяются исключительно подписанными сторонами договорами и согласованной банковской процедурой.",
            ],
          },
          {
            id: "kyc",
            icon: ShieldCheck,
            label: "KYC / AML / санкции",
            title: "Проверка контрагентов и деловой цели",
            paragraphs: [
              "До раскрытия конфиденциальной информации, организации прямой встречи или продолжения сделки компания может запросить и проверить KYC/KYB-пакет: торговую лицензию, регистрационные документы, учредительные документы, структуру владения и UBO, данные уполномоченного подписанта, сведения о деятельности, источнике средств и источнике благосостояния, подтверждение финансовой возможности и банковские документы.",
              "Объём проверки является риск-ориентированным. Компания вправе запросить дополнительные документы, провести уточняющую проверку, ограничить раскрытие информации, приостановить рассмотрение или отказаться от взаимодействия, если сведения недостаточны, противоречивы либо вызывают обоснованные compliance-риски.",
              "Компания не является банком и не гарантирует банковский клиринг, открытие счёта, выпуск SBLC или иного финансового инструмента, работу escrow-механизма либо одобрение операции третьей стороной. Критерии конкретных банков, контрагентов и государственных органов могут отличаться от внутренней первичной проверки компании.",
            ],
          },
          {
            id: "privacy",
            icon: LockKeyhole,
            label: "Персональные данные",
            title: "Конфиденциальность и обработка данных",
            paragraphs: [
              "При обращении через сайт компания может обрабатывать имя, название компании, рабочий email, выбранный мессенджер, содержание запроса, параметры интереса, а также ограниченные технические данные, необходимые для безопасности и работы сайта. Корпоративные KYC-документы запрашиваются только при необходимости и через согласованный защищённый канал.",
              "Данные используются для обработки обращения, связи с заявителем, оценки деловой цели, KYC/KYB и AML-проверок, подготовки коммерческой коммуникации, защиты от мошенничества, ведения обязательной документации и выполнения применимых требований. Компания не продаёт персональные данные и не использует их для несвязанной рекламы.",
              "Доступ ограничен уполномоченными сотрудниками и поставщиками услуг, действующими по инструкции компании. Данные могут быть раскрыты проверенным контрагентам в рамках согласованной сделки, банкам, профессиональным консультантам или компетентным органам, когда это необходимо по закону, для compliance-проверки или с надлежащим основанием. Международная передача возможна только в пределах, необходимых для указанных целей и с применением подходящих мер защиты.",
              `Для запроса доступа, исправления неточных данных, ограничения обработки или иных вопросов о конфиденциальности напишите на ${shared.email}. Данные хранятся не дольше, чем это нужно для заявленной цели, договорных обязательств, защиты прав компании или обязательного хранения по применимому праву.`,
            ],
          },
          {
            id: "security",
            icon: FileText,
            label: "Документы и безопасность",
            title: "Работа с конфиденциальными файлами",
            paragraphs: [
              "Документы, представленные для проверки, рассматриваются как конфиденциальные деловые материалы. Компания использует разграничение доступа, защищённое хранение, журналирование ключевых действий и контроль целостности файлов там, где это предусмотрено рабочим процессом.",
              "Не направляйте пароли, реквизиты платёжных карт, ключи доступа или иные избыточные секретные сведения через обычную форму сайта. Для KYC/AML-пакета, подписанной анкеты или чувствительных приложений используйте только специально выданный защищённый канал и следуйте инструкции компании.",
            ],
          },
          {
            id: "cookies",
            icon: Cookie,
            label: "Cookies и аналитика",
            title: "Технические данные сайта",
            paragraphs: [
              "Сайт может использовать необходимые технические технологии для корректной работы, защиты от злоупотреблений и сохранения выбранных настроек, а также аналитические инструменты для понимания посещаемости и улучшения сайта. Такие технологии могут обрабатывать ограниченные технические данные, включая тип устройства, браузер, источник перехода и агрегированные сведения о посещении.",
              "Вы можете управлять cookies и локальным хранилищем через настройки своего браузера. Отключение необходимых технологий может повлиять на работу отдельных функций сайта.",
            ],
          },
          {
            id: "terms",
            icon: Scale,
            label: "Условия использования",
            title: "Деловые и правовые оговорки",
            paragraphs: [
              "Материалы сайта принадлежат компании или используются ею на законных основаниях. Их нельзя воспроизводить, распространять или использовать для создания ложного впечатления о партнёрстве без письменного разрешения правообладателя.",
              "Компания вправе обновлять эту информацию, процедуры проверки и доступные каналы связи по мере изменения операционных или compliance-требований. Продолжая использовать сайт или направляя обращение, посетитель подтверждает, что ознакомился с настоящими положениями.",
            ],
          },
        ],
        references: "Официальные справочные источники",
        privacyRef: "UAE Government — Personal Data Protection Law",
        kycRef: "Central Bank of the UAE — CDD / KYC Guidance",
      };
    }

    return {
      heading: "Legal Information & Compliance",
      eyebrow: "MELNIK Commercial Brokers · Dubai, U.A.E.",
      trigger: "Legal & Compliance",
      close: "Close",
      note: "Public information only. The signed transaction documents and applicable law govern a specific transaction.",
      sections: [
        {
          id: "company",
          icon: Landmark,
          label: "Company & role",
          title: "Commercial broker status",
          paragraphs: [
            `${shared.company} acts as a commercial broker in Dubai. Licence: ${shared.licence}. Contact address: ${shared.address}.`,
            "The company arranges business introductions and helps the parties structure communications and counterparty screening. It is not the seller or purchaser of goods, does not take custody of goods, does not control party funds, and does not guarantee that a transaction will be concluded or performed. Where the parties discuss an SBLC, escrow or a commercial discount against an agreed market reference, those terms are determined only by the parties, their banks, any authorised escrow provider and their professional advisers.",
            "Website information is preliminary and informational only. It is not a public offer, investment recommendation, financial-services offer or commitment to transact. Commercial, settlement and delivery terms are determined exclusively by agreements signed by the relevant parties and the agreed banking procedure.",
          ],
        },
        {
          id: "kyc",
          icon: ShieldCheck,
          label: "KYC / AML / sanctions",
          title: "Counterparty and business-purpose screening",
          paragraphs: [
            "Before disclosing confidential information, arranging a direct meeting or progressing a transaction, the company may request and review a KYC/KYB pack. This may include a trade licence, incorporation and constitutional documents, ownership and UBO information, authorised-signatory details, business activity, source of funds and source of wealth information, proof of financial capacity and bank documentation.",
            "The scope of screening is risk-based. The company may request further information, carry out clarification checks, limit information disclosure, pause a review or decline an engagement where information is insufficient, inconsistent or presents a reasonable compliance concern.",
            "The company is not a bank and does not guarantee bank clearance, account opening, issuance of an SBLC or other financial instrument, operation of an escrow arrangement or approval of a transaction by any third party. The criteria of banks, counterparties and public authorities may differ from the company's initial internal review.",
          ],
        },
        {
          id: "privacy",
          icon: LockKeyhole,
          label: "Personal data",
          title: "Privacy and personal-data processing",
          paragraphs: [
            "When an enquiry is submitted, the company may process a name, company name, business email, selected messenger contact, enquiry content and requirements, together with limited technical information required for site security and operation. Corporate KYC documents are requested only when needed and through an agreed secure channel.",
            "Data is used to handle an enquiry, communicate with the applicant, assess business purpose, perform KYC/KYB and AML screening, prepare commercial communications, prevent fraud, maintain required records and meet applicable obligations. The company does not sell personal data or use it for unrelated advertising.",
            "Access is limited to authorised staff and service providers acting under company instructions. Data may be disclosed to verified counterparties in an agreed transaction, banks, professional advisers or competent authorities where required by law, for compliance review or with an appropriate legal basis. International transfers are made only where needed for these purposes and with appropriate safeguards.",
            `To request access, correction of inaccurate data, restriction of processing or to raise another privacy question, contact ${shared.email}. Data is retained no longer than necessary for the stated purpose, contractual obligations, protection of company rights or mandatory retention under applicable law.`,
          ],
        },
        {
          id: "security",
          icon: FileText,
          label: "Documents & security",
          title: "Confidential-file handling",
          paragraphs: [
            "Documents provided for review are treated as confidential business materials. The company uses role-based access, protected storage, audit logging of key actions and file-integrity controls where included in the relevant workflow.",
            "Do not send passwords, payment-card data, access keys or other unnecessary secret information through the ordinary website form. Use only a specifically issued secure channel for a KYC/AML pack, signed questionnaire or sensitive attachment, and follow the company's instructions.",
          ],
        },
        {
          id: "cookies",
          icon: Cookie,
          label: "Cookies & analytics",
          title: "Technical website data",
          paragraphs: [
            "The site may use necessary technical technologies for proper operation, abuse prevention and retaining selected settings, as well as analytics tools to understand site traffic and improve the website. These technologies may process limited technical information such as device type, browser, referral source and aggregated visit information.",
            "You can manage cookies and local storage in your browser settings. Disabling necessary technologies may affect the operation of certain site features.",
          ],
        },
        {
          id: "terms",
          icon: Scale,
          label: "Terms of use",
          title: "Commercial and legal notices",
          paragraphs: [
            "Website materials belong to the company or are used by it on a lawful basis. They may not be reproduced, distributed or used to create a false impression of partnership without the rights holder's written permission.",
            "The company may update this information, its screening procedures and available communication channels as operational or compliance requirements change. By continuing to use the website or sending an enquiry, a visitor acknowledges these provisions.",
          ],
        },
      ],
      references: "Official reference sources",
      privacyRef: "UAE Government — Personal Data Protection Law",
      kycRef: "Central Bank of the UAE — CDD / KYC Guidance",
    };
  }, [address, company, email, isRu, licence]);

  const current = content.sections.find((section) => section.id === active) || content.sections[0];
  const CurrentIcon = current.icon;

  return (
    <>
      <button
        type="button"
        data-testid="legal-compliance-open"
        onClick={() => setOpen(true)}
        className="group mt-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/55 bg-[#D4AF37]/10 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#F7E8A5] transition-all duration-200 hover:border-[#D4AF37] hover:bg-[#D4AF37]/20 active:scale-[0.97]"
      >
        <ShieldCheck className="h-4 w-4" />
        {content.trigger}
        <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
      </button>

      {open && (
        <div
          role="presentation"
          onMouseDown={() => setOpen(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#07080C]/88 p-3 backdrop-blur-md sm:p-6"
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="legal-compliance-title"
            onMouseDown={(event) => event.stopPropagation()}
            data-testid="legal-compliance-modal"
            className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-[1.75rem] border border-[#D4AF37]/30 bg-[#12141C] text-[#F5F2EA] shadow-[0_32px_120px_rgba(0,0,0,0.7)] sm:rounded-[2.5rem]"
          >
            <header className="flex items-start justify-between gap-5 border-b border-white/10 px-5 py-5 sm:px-8 sm:py-7">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37] sm:text-xs">{content.eyebrow}</p>
                <h2 id="legal-compliance-title" className="mt-2 font-serif text-2xl font-semibold tracking-tight text-[#F8F4E6] sm:text-4xl">
                  {content.heading}
                </h2>
              </div>
              <button
                type="button"
                aria-label={content.close}
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-[#F5F2EA]/75 transition-colors hover:border-[#D4AF37]/70 hover:bg-white/5 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="grid min-h-0 flex-1 lg:grid-cols-[245px_minmax(0,1fr)]">
              <nav aria-label={isRu ? "Разделы юридической информации" : "Legal information sections"} className="border-b border-white/10 bg-[#0C0E14] p-3 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:p-5">
                <div className="flex gap-2 overflow-x-auto pb-1 lg:block lg:space-y-1 lg:overflow-visible">
                  {content.sections.map((section) => {
                    const Icon = section.icon;
                    const selected = current.id === section.id;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => setActive(section.id)}
                        className={`flex shrink-0 items-center gap-3 rounded-xl px-3 py-3 text-left text-xs transition-colors lg:w-full ${
                          selected ? "bg-[#D4AF37]/15 text-[#F7E8A5]" : "text-[#F5F2EA]/60 hover:bg-white/5 hover:text-[#F5F2EA]"
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="whitespace-nowrap lg:whitespace-normal">{section.label}</span>
                      </button>
                    );
                  })}
                </div>
              </nav>

              <div className="min-h-0 overflow-y-auto px-5 py-6 sm:px-9 sm:py-9">
                <div className="mx-auto max-w-3xl">
                  <div className="flex items-center gap-3 text-[#D4AF37]">
                    <CurrentIcon className="h-5 w-5" />
                    <span className="text-xs font-semibold uppercase tracking-[0.19em]">{current.label}</span>
                  </div>
                  <h3 className="mt-4 font-serif text-2xl font-semibold text-[#F8F4E6] sm:text-3xl">{current.title}</h3>
                  <div className="mt-6 space-y-4 text-sm leading-7 text-[#F5F2EA]/78 sm:text-[15px]">
                    {current.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {current.id === "privacy" && (
                      <a href="/privacy-policy" className="inline-flex text-sm font-semibold text-[#F5E5AB] underline decoration-[#D4AF37]/55 underline-offset-4 transition-colors hover:text-white">
                        {isRu ? "Открыть полную публичную Политику конфиденциальности" : "Open the full public Privacy Policy"}
                      </a>
                    )}
                  </div>

                  <aside className="mt-8 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.07] p-4 text-xs leading-relaxed text-[#F5F2EA]/68 sm:p-5">
                    <p className="font-semibold uppercase tracking-[0.16em] text-[#F3E5AB]">{content.references}</p>
                    <div className="mt-3 flex flex-col gap-2">
                      <a className="underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#F7E8A5]" href={UAE_PDPL_URL} target="_blank" rel="noreferrer">
                        {content.privacyRef}
                      </a>
                      <a className="underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#F7E8A5]" href={UAE_KYC_URL} target="_blank" rel="noreferrer">
                        {content.kycRef}
                      </a>
                    </div>
                  </aside>
                  <p className="mt-6 border-t border-white/10 pt-5 text-xs leading-relaxed text-[#F5F2EA]/48">{content.note}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};
